import React, { useContext, useState } from 'react';
import Modal from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link/link';
import { ProofOfDeliveryDetails } from 'src/stores/ProofOfDeliveryDetails';
import PackageDeliveryDetailsContext, { SignatureDetails } from 'src/stores/PackageDeliveryDetails';
import {
  POD_IMAGE_ERROR_METRIC,
  POD_IMAGE_SUCCESS_METRIC,
  POD_IMAGE_VALIDATION_ERROR_METRIC, 
  SOD_ERROR_METRIC, 
  SOD_SUCCESS_METRIC,
  SOD_VALIDATION_ERROR_METRIC,
  STATES,
} from 'src/utils/Constants';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/helpers';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from 'src/utils/Constants';
import { logger } from 'src/logger';
import { trimZipCode } from 'src/utils/ZipCodeUtils';
import { publishSingleMetricCount } from 'src/metrics';
import { sanitizeText } from 'src/utils/SecurityUtils';

interface ZipCodeModalProps {
  trackingNumber: string;
  shippingCountry?: string | undefined;
  hasPhotoOnDelivery?: boolean;
  hasSignatureOnDelivery?: boolean;
}

const ZipCodeModal: React.FC<ZipCodeModalProps> = ({ 
  trackingNumber, 
  shippingCountry, 
  hasPhotoOnDelivery = false,
  hasSignatureOnDelivery = false 
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [zipCode, setZipCode] = useState<string>('');
  const [proofOfDeliveryDetails, setProofOfDeliveryDetails] =
    useState<ProofOfDeliveryDetails | null>(null);
  const [signatureDetails, setSignatureDetails] = useState<SignatureDetails | null>(null);
  const [error, setError] = useState<string | undefined>('');
  const [zipCodeError, setZipCodeError] = useState<string | undefined>('');
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const packageDeliveryDetails = useContext(PackageDeliveryDetailsContext);
  const handleOpen = (): void => {
    void logger.info(`ZipCode modal opened for tracking number ${trackingNumber} 
    - flow: ${hasSignatureOnDelivery ? 'signature on delivery' : 'photo on delivery'}`);
    setOpen(true);
  };
  const handleClose = (): void => {
    void logger.info(`ZipCode modal closed for tracking number ${trackingNumber}`);
    setOpen(false);
  };
  const genericZipCodeRegex = /^[A-Z0-9\s-]{2,10}([A-Z]{2})?$/i;

  // Don't render anything if neither POD nor SOD is available
  if (!hasPhotoOnDelivery && !hasSignatureOnDelivery) {
    return null;
  }

  const getLinkText = (): string | undefined => {
    if (hasSignatureOnDelivery) {
      return t(bundle, 'swiship_view_signature_details');
    } else {
      return t(bundle, 'swiship_view_delivery_photo');
    }
  };

  const getModalTitle = (): string | undefined => {
    if (hasSignatureOnDelivery) {
      return t(bundle, 'swiship_signature_details');
    } else {
      return t(bundle, 'swiship_delivery_photo');
    }
  };

  const handleZipCodeChange = (value: string): void => {
    setError('');
    setZipCode(value); // Don't trim - preserve spaces for validation

    if (value.length >= 3) {
      if (genericZipCodeRegex.test(value)) {
        setZipCodeError('');
      } else {
        setZipCodeError(t(bundle, 'swiship_invalid_zip_code_format'));
        void logger.warn('Invalid ZipCode format ' + zipCode + ' for tracking number ' + trackingNumber);
        const validationErrorMetric = hasSignatureOnDelivery 
          ? SOD_VALIDATION_ERROR_METRIC 
          : POD_IMAGE_VALIDATION_ERROR_METRIC;
        publishSingleMetricCount(validationErrorMetric);
      }
    } else {
      setZipCodeError('');
    }
  };

  const handleError = (errorMessage: string | undefined): void => {
    void logger.error(`Delivery Error: trackingId=${trackingNumber}, error=${errorMessage}, isPOD=${hasPhotoOnDelivery}, isSOD=${hasSignatureOnDelivery}`);
    setError(errorMessage);
    setProofOfDeliveryDetails(null);
    
    // Publish the appropriate error metric based on a delivery type
    if (hasSignatureOnDelivery) {
      publishSingleMetricCount(SOD_ERROR_METRIC);
    } else {
      publishSingleMetricCount(POD_IMAGE_ERROR_METRIC);
    }
  };

  const handlePODSuccess = (): void => {
    if (
      Array.isArray(packageDeliveryDetails.getProofOfDeliveryDetails) &&
      packageDeliveryDetails.getProofOfDeliveryDetails.length === 0
    ) {
      handleError(t(bundle, 'swiship_something_went_wrong'));
    } else {
      setProofOfDeliveryDetails(packageDeliveryDetails.getProofOfDeliveryDetails);
      publishSingleMetricCount(POD_IMAGE_SUCCESS_METRIC);
      setError('');
    }
  };

  const handleSODSuccess = (): void => {
    publishSingleMetricCount(SOD_SUCCESS_METRIC);
    setError('');
    setProofOfDeliveryDetails(null); // Clear POD data when showing SOD
    setSignatureDetails(packageDeliveryDetails.getSignatureDetails);
    packageDeliveryDetails.setLoadingState(STATES.DONE);
  };

  const handleSubmit = async (): Promise<void> => {
    if (zipCodeError) {
      return; // Don't submit if there's a zip code error
    }
    
    setError('');
    
    try {
      if (!trackingNumber) {
        handleError(t(bundle, 'swiship_invalid_tracking_id'));
        return;
      }

      publishSingleMetricCount('ZipCodeModalSubmit');
      packageDeliveryDetails.setLoadingState(STATES.PENDING);
      const trimmedZipCode = trimZipCode(zipCode, shippingCountry);
      
      void logger.info(`Delivery API Request: trackingId=${trackingNumber}, userZipCode="${zipCode}", trimmedZipCode="${trimmedZipCode}", country=${shippingCountry}, isPOD=${hasPhotoOnDelivery}, isSOD=${hasSignatureOnDelivery}`);
      
      await packageDeliveryDetails.fetchPackageDeliveryDetails(
        trackingNumber, 
        trimmedZipCode,
        hasPhotoOnDelivery,
        hasSignatureOnDelivery
      );

      // Handle API response
      if (packageDeliveryDetails.getErrorMessage) {
        void logger.warn('Zipcode submit error from API for tracking id: '
        + trackingNumber + (hasSignatureOnDelivery ? ' for signatureOnDelivery' : ' for photo on delivery')
        + ', error: ' + packageDeliveryDetails.getErrorMessage);
        packageDeliveryDetails.setLoadingState(STATES.ERROR);
        handleError(t(bundle, 'swiship_something_went_wrong'));
      } else {
        // Check what we actually received
        if (hasSignatureOnDelivery) {
          if (packageDeliveryDetails?.getSignatureDetails) {
            handleSODSuccess();
          } else {
            packageDeliveryDetails.setLoadingState(STATES.ERROR);
            handleError(t(bundle, 'swiship_no_delivery_details'));
          }
        } else if (hasPhotoOnDelivery) {
          if (packageDeliveryDetails?.getProofOfDeliveryDetails) {
            handlePODSuccess();
          } else {
            packageDeliveryDetails.setLoadingState(STATES.ERROR);
            handleError(t(bundle, 'swiship_no_delivery_details'));
          }
        }
      }
    } catch (err: any) {
      packageDeliveryDetails.setLoadingState(STATES.ERROR);
      handleError(STATES.ERROR);
      await logger.error('Error during submission:', err);
    }
  };

  const hasPhotoData = (): boolean => {
    return hasPhotoOnDelivery && 
           proofOfDeliveryDetails !== null && 
           proofOfDeliveryDetails.getProofOfDeliveryDetails.length > 0;
  };

  const hasSignatureData = (): boolean => {
    return hasSignatureOnDelivery && signatureDetails !== null;
  };

  const hasAnyDeliveryData = (): boolean => {
    return hasPhotoData() || hasSignatureData();
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
    if (event.key === 'Enter') {
      await handleSubmit();
    }
  };

  return (
    <>
      <Link 
        onClick={handleOpen} 
        type="primary" 
        data-id={hasSignatureOnDelivery ? "view-signature-details-link" : "view-delivery-photo-link"}
      >
        {getLinkText()}
      </Link>
      <Modal
        title={getModalTitle()}
        open={open}
        onClose={handleClose}
        scrollContainer="viewport"
        closeLabel={t(bundle, 'swiship_close_button')}
        aria-describedby="modal-description"
        width={500}
        data-id="delivery-photo-modal"
      >
        {!error && hasAnyDeliveryData() ? (
          <>
            {hasPhotoData() && proofOfDeliveryDetails?.getProofOfDeliveryDetails.map((proof, index) => (
              <div key={index}>
                {proof.imageType ? (
                  <>
                    <img
                      width="100%"
                      height="100%"
                      src={proof.imageUrl}
                      alt={t(bundle, 'swiship_delivery_proof')}
                      className="photo-on-delivery-image"
                    />
                  </>
                ) : (
                  <Text>{t(bundle, 'swiship_something_went_wrong')}</Text>
                )}
              </div>
            ))}
            {hasSignatureData() && (
              <div>
                <Text type="b300">{t(bundle, 'swiship_signed_by_label')}</Text>
                <Text type="b400">{sanitizeText(signatureDetails?.signedBy)}</Text>
              </div>
            )}
          </>
        ) : (
          <>
            {packageDeliveryDetails.getLoadingState === STATES.PENDING ? (
              <Column alignmentHorizontal="center">
                <Loader data-testid="loader"></Loader>
              </Column>
            ) : (
              <>
                <Text>{t(bundle, hasSignatureOnDelivery ? 'swiship_enter_zip_code_label_sod' : 'swiship_enter_zip_code_label_pod')}</Text>
                <Column spacingInset="200 none">
                  <Input
                    id="zip-code"
                    data-testid="zip-code"
                    type="text"
                    placeholder={t(bundle, 'swiship_zip_code_placeholder')}
                    value={zipCode}
                    onChange={handleZipCodeChange}
                    onKeyDown={handleKeyDown}
                  />
                </Column>
                {zipCodeError && (
                  <Text color="error" className="zipcode-error">
                    {zipCodeError}
                  </Text>
                )}
                {error && (
                  <Text color="error">
                    {error === STATES.ERROR
                      ? t(bundle, 'swiship_something_went_wrong')
                      : error}
                  </Text>
                )}
                <br />
                <Button onClick={handleSubmit} id="delivery-photo-submit">
                  {t(bundle, 'swiship_submit_button')}
                </Button>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default ZipCodeModal;
